import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { dateAsString } from '../shared/date.helper';
import { escapeHtml } from '../shared/html.helper';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

export enum FilterType {
  DATE = 'date',
  TEXTWITHOR = 'textWithOr',
  SELECT = 'select',
  TEXT = 'text',
  NUMBERWITHCONDITION = 'numberWightGreatterThanAndLessThan',
}

interface Option {
  id: any;
  label: string;
}

export interface Filter {
  disabled?: boolean;
  name: string;
  label: string;
  type: FilterType;
  multi?: boolean;
  options?: Option[];
  group: string;
  hasOperator?: boolean;
  operatorLabel?: string;
  placeholder?: string;
  validation?: {
    type: 'regex';
    pattern: string;
    message: string;
  };
}

interface CurrentFilter {
  key: string;
  value: any;
  operator: string;
}

@Component({
  selector: 'app-search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.scss'],
})
export class SearchFilterComponent implements OnInit, OnDestroy {
  @Output() filtersUpdated = new EventEmitter<any>();
  @Output() filtersRemoved = new EventEmitter<any>();
  dateValueForm: UntypedFormGroup;
  private subscription = new Subscription();
  private subject = new Subject();
  private ngUnsubscribe = new Subject();

  dateConfig = { dateInputFormat: 'MM-DD-YYYY', customTodayClass: 'date-today', hour: 23 };
  isEmpty = false;
  validationErrors = {};
  _currentFilters: CurrentFilter[] = [];
  set currentFilters(value: CurrentFilter[]) {
    this._currentFilters = value;
    this.isEmpty = this.hasEmpty(this._currentFilters);
  }

  get currentFilters(): CurrentFilter[] {
    this.isEmpty = this.hasEmpty(this._currentFilters);
    return this._currentFilters;
  }

  private _filters: Filter[];
  @Input() set filters(value: Filter[]) {
    for (const filter of value) {
      filter.disabled = !!this.currentFilters.find(f => f.key === filter.name);
    }
    this._filters = value;
  }

  @Input() set params(value) {
    this.setSearch(value);
  }
  get filters(): Filter[] {
    return this._filters;
  }

  hasEmpty(arr) {
    return arr.some(obj => {
      return obj.key === null;
    });
  }

  constructor(private fb: UntypedFormBuilder) {}

  @ViewChildren('editableDivs') editableDivs!: QueryList<ElementRef>;

  ngOnInit() {
    this.dateValueForm = this.fb.group({ date: null });
  }

  setSearch(queryParams) {
    if (queryParams) {
      const initialFilters = [];
      for (const key in queryParams) {
        if (queryParams.hasOwnProperty(key)) {
          if (key.endsWith('Operator')) {
            continue;
          }

          let value = queryParams[key];
          const filter = this.filters.find(f => f.name === key);
          if (filter) {
            let operator = '';
            if (filter.type === 'date') {
              value = dateAsString(value);
              if (queryParams[key + 'Operator']) {
                operator = queryParams[key + 'Operator'];
              }
            }
            if (filter.type === 'numberWightGreatterThanAndLessThan') {
              if (queryParams[key + 'Operator']) {
                operator = queryParams[key + 'Operator'];
              }
              value = value.toString();
            } else if (filter.type === 'textWithOr') {
              value = Number(value) || value;
              setTimeout(() => {
                const editableDiv = (this.editableDivs || []).find(f => f.nativeElement.id === key);
                if (editableDiv) {
                  editableDiv.nativeElement.innerHTML = this.parse(value);
                }
              }, 0);
              if (queryParams[key + 'Operator'] && filter.hasOperator) {
                operator = queryParams[key + 'Operator'];
              }
            } else if (filter.type === 'select' && !filter.multi) {
              value = Number(value) || value;
            }
            initialFilters.push({
              key: key,
              value: value,
              operator: operator,
            });
          }
        }
      }
      if (initialFilters.length > 0) {
        this.currentFilters = [...initialFilters];
        this.filtersUpdated.emit(this.currentFilters);
      } else {
        this.currentFilters = [{ key: null, value: '', operator: '' }];
      }
      this.disableFilters();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.subscription.unsubscribe();
  }

  filterChanged(filter: string, key: string, changeType: string) {
    this.disableFilters();
    const config = this.getConfiguration(key);
    if (changeType === 'key') {
      this.currentFilters.find(f => f.key === filter).value = '';
      if (filter) {
        // Set default operator
        if (config.type === FilterType.DATE || config.type === FilterType.NUMBERWITHCONDITION) {
          this.currentFilters.find(f => f.key === filter).operator = 'eq';
        } else if (config.type === FilterType.TEXT && config.hasOperator) {
          this.currentFilters.find(f => f.key === filter).operator = 'gt';
        }
      } else {
        const emptyFilters = this.currentFilters.filter(f => f.key !== null);
        this.currentFilters = emptyFilters;
        if (emptyFilters.length === 0) {
          this.currentFilters.push({ key: null, value: '', operator: '' });
        }
      }
    }

    if (changeType === 'operator' || changeType === 'value') {
      this.subscription.unsubscribe();
      this.subject = new Subject();
      this.subscription = this.subject.pipe(debounceTime(1000), takeUntil(this.ngUnsubscribe)).subscribe(() => {
        if (config.type === FilterType.DATE) {
          const currentFilter = this.currentFilters.find(f => f.key === key);
          if (currentFilter?.value === '-' || currentFilter?.value === '') {
            return false;
          }
          if (currentFilter?.value) {
            const currentValue = currentFilter.value;
            currentFilter.value = dateAsString(currentValue);
          }
          this.filtersUpdated.emit(this.currentFilters);
          this.updateQueryParams();
        } else if (config.type === FilterType.TEXT) {
          if (config.validation) {
            const currentFilter = this.currentFilters.find(f => f.key === key);
            if (currentFilter.value || currentFilter.value === '') {
              if (config.validation.type === 'regex') {
                const regex = new RegExp(config.validation.pattern);
                const isValid = regex.test(currentFilter.value);
                if (isValid || currentFilter.value === '') {
                  this.filtersUpdated.emit(this.currentFilters);
                  this.validationErrors[key] = false;
                } else {
                  this.validationErrors[key] = true;
                  return;
                }
                this.updateQueryParams();
              }
            }
          } else {
            this.filtersUpdated.emit(this.currentFilters);
            this.updateQueryParams();
          }
        } else {
          this.filtersUpdated.emit(this.currentFilters);
          this.updateQueryParams();
        }
      });
      this.subject.next();
    }
  }

  resetAll() {
    this.currentFilters = [{ key: null, value: '', operator: '' }];
    this.disableFilters();
    this.updateQueryParams();
    this.filtersUpdated.emit(this.currentFilters);
  }

  setDate($event, filter, value) {
    if ($event) {
      filter.value = $event;
      this.filterChanged($event, filter.key, value);
    }
  }

  disableFilters() {
    const filters = [...this.filters];
    for (const filter of filters) {
      filter.disabled = !!this.currentFilters.find(f => f.key || '' === filter.name);
    }
    this.filters = [...filters];
  }

  updateQueryParams() {
    const queryParams = {};
    for (const filter of this.currentFilters) {
      if (!filter.key) {
        continue;
      }
      if (filter.operator) {
        queryParams[filter.key + 'Operator'] = filter.operator;
      }
      queryParams[filter.key] = filter.value;
    }

    this.filtersUpdated.emit(this.currentFilters);
  }

  // Click functions
  onClickClearFilter(key: string) {
    this.currentFilters = [...this.currentFilters.filter(f => f.key !== key)];
    this.disableFilters();
    this.updateQueryParams();
    if (this.currentFilters.length === 0) {
      this.currentFilters.push({ key: null, value: '', operator: '' });
    }
    this.filtersUpdated.emit(this.currentFilters);
  }

  onClickShowButton() {
    this.currentFilters.push({ key: null, value: '', operator: '' });
  }

  // HTML functions
  getConfiguration(name: string) {
    const filters = [...this.filters];
    return filters.find(f => f.name === name);
  }

  showAddButton(filter, i) {
    const lastFilter = this.currentFilters.length - 1 === i;
    const filterNotEmpty = !this.isEmpty && filter.value !== '';
    const allFiltersUsed = this.currentFilters.length >= this.filters.length;

    return lastFilter && filterNotEmpty && !allFiltersUsed;
  }

  showAddLabel(i) {
    return i > 0;
  }

  getBootstrapClass(filter, i) {
    let bootstrapClass = 'col-md-11';
    if (this.showAddLabel(i) && this.showAddButton(filter, i)) {
      bootstrapClass = 'col-md-10';
    }
    if (!this.showAddLabel(i) && !this.showAddButton(filter, i)) {
      bootstrapClass = 'col-md-12';
    }
    if (filter.value !== '') {
      bootstrapClass += ' wrap';
    }
    return bootstrapClass;
  }

  // Text input
  onContentEdit(filter): void {
    const editor = this.editableDivs.find(f => f.nativeElement.id === filter.key).nativeElement;
    const currentText = editor.textContent || '';
    filter.value = currentText;
    this.currentFilters.find(f => f.key === filter.key).value = currentText;
    this.filterChanged(filter, filter.key, 'value');

    //get current cursor position
    const sel = window.getSelection();
    const node = sel.focusNode;
    const offset = sel.focusOffset;
    const pos = this.getCursorPosition(editor, node, offset, { pos: 0, done: false });
    if (offset === 0) pos.pos += 0.5;

    editor.innerHTML = this.parse(editor.innerText);

    // restore the position
    sel.removeAllRanges();
    const range = this.setCursorPosition(editor, document.createRange(), {
      pos: pos.pos,
      done: false,
    });
    range.collapse(true);
    sel.addRange(range);
  }

  parse(textWithOr) {
    if (typeof textWithOr === 'number') {
      textWithOr = textWithOr.toString(); // Convert number to string
    }
    if (!textWithOr) {
      return '';
    }
    const highlightStyle =
      'border-radius: 3px; font-weight: bold; background-color: #a4a2a2; color: #ffffff; font-size: 12px; padding: 1px 3px 1px 3px;';

    return escapeHtml(String(textWithOr)).replace(/\b(OR|or|Or|oR)\b/g, `<span style="${highlightStyle}">OR</span>`);
  }

  // get the cursor position from .editor start
  getCursorPosition(parent, node, offset, stat) {
    if (stat.done) return stat;

    let currentNode = null;
    if (parent.childNodes.length == 0) {
      stat.pos += parent.textContent.length;
    } else {
      for (let i = 0; i < parent.childNodes.length && !stat.done; i++) {
        currentNode = parent.childNodes[i];
        if (currentNode === node) {
          stat.pos += offset;
          stat.done = true;
          return stat;
        } else this.getCursorPosition(currentNode, node, offset, stat);
      }
    }
    return stat;
  }

  //find the child node and relative position and set it on range
  setCursorPosition(parent, range, stat) {
    if (stat.done) return range;

    if (parent.childNodes.length == 0) {
      if (parent.textContent.length >= stat.pos) {
        range.setStart(parent, stat.pos);
        stat.done = true;
      } else {
        stat.pos = stat.pos - parent.textContent.length;
      }
    } else {
      for (let i = 0; i < parent.childNodes.length && !stat.done; i++) {
        const currentNode = parent.childNodes[i];
        this.setCursorPosition(currentNode, range, stat);
      }
    }
    return range;
  }
}
